import { Fragment, useState } from 'react';
import { Badge, Card, Collapse, Table } from 'react-bootstrap';
import { matchesSafetyControlLane, type AISafetyRun } from './aiSafety.utils';

export type SafetyEvidenceRow = {
  control?: string;
  rule_id?: string;
  severity?: string;
  matched_text?: string;
  context?: string;
  [key: string]: unknown;
};

function severityBadge(severity: unknown) {
  const s = String(severity ?? '')
    .trim()
    .toUpperCase();
  if (s === 'HIGH' || s === 'CRITICAL') return <Badge bg="danger">{s || '—'}</Badge>;
  if (s === 'MEDIUM' || s === 'MODERATE') {
    return (
      <Badge bg="warning" text="dark">
        {s}
      </Badge>
    );
  }
  if (s === 'LOW') return <Badge bg="success">{s}</Badge>;
  return <Badge bg="secondary">{s || '—'}</Badge>;
}

function readEvidence(run: AISafetyRun | null | undefined): SafetyEvidenceRow[] {
  if (!run) return [];
  const raw = run.evidence;
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is SafetyEvidenceRow => row != null && typeof row === 'object');
}

function readDeveloperGuidance(run: AISafetyRun | null | undefined): {
  perControl: Record<string, unknown>;
  priorityActions: unknown[];
} {
  const dg = run?.developer_guidance;
  if (!dg || typeof dg !== 'object' || Array.isArray(dg)) {
    return { perControl: {}, priorityActions: [] };
  }
  const obj = dg as Record<string, unknown>;
  const perControl =
    obj.per_control && typeof obj.per_control === 'object' && !Array.isArray(obj.per_control)
      ? (obj.per_control as Record<string, unknown>)
      : {};
  const priorityActions = Array.isArray(obj.priority_actions) ? obj.priority_actions : [];
  return { perControl, priorityActions };
}

function guidanceForControl(
  perControl: Record<string, unknown>,
  control: string | undefined
): Record<string, unknown> | null {
  if (!control) return null;
  const direct = perControl[control];
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as Record<string, unknown>;
  }
  const normalized = control.toLowerCase().replace(/\s+/g, '_');
  for (const [key, val] of Object.entries(perControl)) {
    if (key.toLowerCase() === normalized && val && typeof val === 'object') {
      return val as Record<string, unknown>;
    }
  }
  return null;
}

function renderGuidanceBody(guidance: Record<string, unknown> | null, priorityActions: unknown[]) {
  if (!guidance && priorityActions.length === 0) {
    return <p className="small text-muted mb-0">No developer guidance available for this control.</p>;
  }

  return (
    <div className="small">
      {guidance ? (
        <>
          {guidance.summary != null ? (
            <p className="mb-2">
              <strong>Summary:</strong> {String(guidance.summary)}
            </p>
          ) : null}
          {guidance.remediation != null ? (
            <p className="mb-2">
              <strong>Remediation:</strong> {String(guidance.remediation)}
            </p>
          ) : null}
          {Array.isArray(guidance.actions) ? (
            <ul className="mb-2">
              {(guidance.actions as unknown[]).map((a, i) => (
                <li key={i}>{String(a)}</li>
              ))}
            </ul>
          ) : null}
          {guidance.recommendation != null ? (
            <p className="mb-0">
              <strong>Recommendation:</strong> {String(guidance.recommendation)}
            </p>
          ) : null}
        </>
      ) : null}
      {priorityActions.length > 0 ? (
        <div className="mt-2">
          <strong>Priority actions</strong>
          <ul className="mb-0 mt-1">
            {priorityActions.map((a, i) => (
              <li key={i}>{typeof a === 'string' ? a : JSON.stringify(a)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function AISafetyEvidenceAccordion({ run, lane }: { run: AISafetyRun | null | undefined; lane?: 'ml' | 'llm' }) {
  const allEvidence = readEvidence(run);
  const evidence = lane
    ? allEvidence.filter(row => matchesSafetyControlLane(String(row.control ?? row.Control ?? ''), lane))
    : allEvidence;
  const { perControl, priorityActions } = readDeveloperGuidance(run);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (evidence.length === 0) {
    return (
      <Card className="border-0 shadow-sm mt-3">
        <Card.Body>
          <h6 className="mb-1">AI Safety Evidence &amp; Guidance</h6>
          <p className="small text-muted mb-0">No evidence rows recorded for this evaluation run.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm mt-3">
      <Card.Body>
        <h6 className="mb-1">AI Safety Evidence &amp; Guidance</h6>
        <p className="small text-muted mb-3">
          Inspect flagged segments and expand a row for remediation guidance mapped to that control.
        </p>
        <div className="table-responsive">
          <Table bordered hover size="sm" className="align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '14%' }}>Control</th>
                <th style={{ width: '12%' }}>Rule ID</th>
                <th style={{ width: '10%' }}>Severity</th>
                <th style={{ width: '32%' }}>Matched Text</th>
                <th style={{ width: '32%' }}>Context</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((row, idx) => {
                const control = String(row.control ?? row.Control ?? '');
                const isOpen = openIndex === idx;
                return (
                  <Fragment key={`${control}-${row.rule_id ?? idx}`}>
                    <tr
                      role="button"
                      tabIndex={0}
                      className={isOpen ? 'table-active' : undefined}
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenIndex(isOpen ? null : idx);
                        }
                      }}
                    >
                      <td className="fw-semibold">{control || '—'}</td>
                      <td>{String(row.rule_id ?? row.ruleId ?? '—')}</td>
                      <td>{severityBadge(row.severity ?? row.Severity)}</td>
                      <td>
                        <small className="text-break">{String(row.matched_text ?? row.matchedText ?? '—')}</small>
                      </td>
                      <td>
                        <small className="text-muted text-break">{String(row.context ?? row.Context ?? '—')}</small>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="p-0 border-0">
                        <Collapse in={isOpen}>
                          <div className="p-3 bg-light border-top">
                            <div className="fw-semibold small mb-2">Developer Guidance — {control || 'Control'}</div>
                            {renderGuidanceBody(guidanceForControl(perControl, control), isOpen ? priorityActions : [])}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

export default AISafetyEvidenceAccordion;
